import React, { createContext, useContext, useState, useMemo } from 'react';
import { createTheme } from '@mui/material/styles';

const ThemeContext = createContext();

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeContextProvider');
  }
  return context;
};

// Dark palette (default) — GitHub Dark inspired
const darkPalette = {
  mode: 'dark',
  primary: { main: '#58a6ff' },
  secondary: { main: '#f78166' },
  background: { default: '#0d1117', paper: '#161b22' },
  text: { primary: '#e6edf3', secondary: '#8b949e' },
  success: { main: '#3fb950' },
  error: { main: '#f85149' },
  warning: { main: '#d29922' },
  divider: '#21262d',
};

// Light palette
const lightPalette = {
  mode: 'light',
  primary: { main: '#0969da' },
  secondary: { main: '#cf222e' },
  background: { default: '#f6f8fa', paper: '#ffffff' },
  text: { primary: '#1f2328', secondary: '#656d76' },
  success: { main: '#1a7f37' },
  error: { main: '#cf222e' },
  warning: { main: '#9a6700' },
  divider: '#d1d9e0',
};

export const ThemeContextProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    const stored = localStorage.getItem('themeMode');
    return stored === 'light' || stored === 'dark' ? stored : 'dark';
  });

  const toggleTheme = () => {
    setMode((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('themeMode', next);
      return next;
    });
  };

  const theme = useMemo(() => {
    const palette = mode === 'dark' ? darkPalette : lightPalette;
    return createTheme({
      palette,
      typography: {
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif',
      },
      shape: {
        borderRadius: 10,
      },
      components: {
        MuiCard: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
              border: `1px solid ${palette.divider}`,
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
            },
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              backgroundImage: 'none',
              borderRight: `1px solid ${palette.divider}`,
            },
          },
        },
      },
    });
  }, [mode]);

  const value = useMemo(() => ({ mode, toggleTheme, theme }), [mode, theme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
